import React, { useState, useEffect, useRef } from 'react';
import type { Bed, Room } from '../lib/api';
import { OperaWindow, OperaSection, OperaField, type SideButton } from './OperaWindow';

interface ProfileSuggestion {
  id: string;
  workingName: string;
  phone: string | null;
}

interface Props {
  bed: Bed;
  room: Room;
  onClose: () => void;
  onCreated: () => void;
}

function todayLocal(): string {
  return new Date().toISOString().slice(0, 10);
}
function tomorrowLocal(): string {
  return new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
}
function dayName(iso: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-AU', {
    weekday: 'long', timeZone: 'Australia/Sydney',
  });
}

export function NewStayModal({ bed, room, onClose, onCreated }: Props) {
  const [nameQuery, setNameQuery] = useState('');
  const [suggestions, setSuggestions] = useState<ProfileSuggestion[]>([]);
  const [chosenProfile, setChosenProfile] = useState<ProfileSuggestion | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [phone, setPhone] = useState('');
  const [arrivalDate, setArrivalDate] = useState(todayLocal());
  const [departureDate, setDepartureDate] = useState(tomorrowLocal());
  const [rateType, setRateType] = useState<'NIGHTLY' | 'WEEKLY'>('NIGHTLY');
  const [nightlyRate, setNightlyRate] = useState('180');
  const [weeklyRate, setWeeklyRate] = useState('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced profile typeahead
  useEffect(() => {
    if (chosenProfile) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (nameQuery.trim().length < 2) { setSuggestions([]); return; }
    debounceTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/profiles?q=${encodeURIComponent(nameQuery.trim())}`);
        if (res.ok) setSuggestions(await res.json());
      } catch { /* ignore */ }
    }, 250);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [nameQuery, chosenProfile]);

  function pickSuggestion(p: ProfileSuggestion) {
    setChosenProfile(p);
    setNameQuery(p.workingName);
    setPhone(p.phone || '');
    setShowSuggestions(false);
  }
  function clearSelection() {
    setChosenProfile(null);
    setShowSuggestions(true);
  }

  async function handleSubmit() {
    setError(null);
    const trimmedName = nameQuery.trim();
    if (!trimmedName) { setError('Working name is required.'); return; }
    const rate = parseFloat(nightlyRate);
    if (isNaN(rate) || rate < 0) { setError('Nightly rate must be a positive number.'); return; }

    const body: Record<string, unknown> = {
      bedId: bed.id, arrivalDate, departureDate, rateType,
      nightlyRate: rate, notes: notes.trim() || undefined,
    };
    if (rateType === 'WEEKLY' && weeklyRate.trim()) {
      const wr = parseFloat(weeklyRate);
      if (!isNaN(wr)) body.weeklyRate = wr;
    }
    if (chosenProfile) {
      body.existingProfileId = chosenProfile.id;
    } else {
      body.newProfileName = trimmedName;
      if (phone.trim()) body.newProfilePhone = phone.trim();
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/stays', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || `Request failed: HTTP ${res.status}`);
        setSubmitting(false);
        return;
      }
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  const bedDescription = room.displayName
    ? `Room ${room.number} (${room.displayName}) — ${bed.label}`
    : `Room ${room.number} — ${bed.label}`;

  const sideButtons: SideButton[] = [
    { label: 'Save',  onClick: handleSubmit, primary: true, disabled: submitting },
    { label: 'Close', onClick: onClose, disabled: submitting },
  ];

  // No action strip on create (we haven't created the record yet)
  return (
    <OperaWindow
      title="NEW ACCOMMODATION BOOKING"
      status={bedDescription.toUpperCase()}
      onClose={onClose}
      sideButtons={sideButtons}
    >
      {error && <div className="modal-error">{error}</div>}

      <OperaSection title="Guest">
        <OperaField label="Working name" required>
          <div className="typeahead-wrap">
            <input
              type="text"
              value={nameQuery}
              onChange={e => {
                setNameQuery(e.target.value);
                if (chosenProfile && e.target.value !== chosenProfile.workingName) clearSelection();
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Start typing (e.g. Scarlett)…"
              autoComplete="off"
            />
            {chosenProfile && (
              <div className="typeahead-chosen">
                ✓ Existing profile
                <button type="button" className="link" onClick={clearSelection}>change</button>
              </div>
            )}
            {!chosenProfile && showSuggestions && suggestions.length > 0 && (
              <div className="typeahead-list">
                {suggestions.map(s => (
                  <div key={s.id} className="typeahead-item" onClick={() => pickSuggestion(s)}>
                    <strong>{s.workingName}</strong>
                    {s.phone && <span style={{ color: 'var(--text-tertiary)', marginLeft: 8 }}>{s.phone}</span>}
                  </div>
                ))}
                <div className="typeahead-divider" />
                <div className="typeahead-item new" onClick={() => setShowSuggestions(false)}>
                  + Create new profile: <strong>{nameQuery.trim()}</strong>
                </div>
              </div>
            )}
          </div>
        </OperaField>

        {!chosenProfile && (
          <OperaField label="Phone">
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="04XX XXX XXX" />
          </OperaField>
        )}
      </OperaSection>

      <OperaSection title="Stay">
        <OperaField label="Arrival" required status={dayName(arrivalDate)}>
          <input type="date" value={arrivalDate} onChange={e => setArrivalDate(e.target.value)} />
        </OperaField>
        <OperaField label="Departure" required status={dayName(departureDate)}>
          <input type="date" value={departureDate} onChange={e => setDepartureDate(e.target.value)} />
        </OperaField>
      </OperaSection>

      <OperaSection title="Rate">
        <OperaField label="Rate type">
          <select value={rateType} onChange={e => setRateType(e.target.value as 'NIGHTLY' | 'WEEKLY')}>
            <option value="NIGHTLY">Nightly</option>
            <option value="WEEKLY">Weekly</option>
          </select>
        </OperaField>
        <OperaField label="Nightly rate" required hint="AUD">
          <input type="number" min="0" step="10" value={nightlyRate} onChange={e => setNightlyRate(e.target.value)} />
        </OperaField>
        {rateType === 'WEEKLY' && (
          <OperaField label="Weekly rate" hint="AUD">
            <input type="number" min="0" step="50" value={weeklyRate} onChange={e => setWeeklyRate(e.target.value)} placeholder="980" />
          </OperaField>
        )}
      </OperaSection>

      <OperaSection title="Notes">
        <textarea
          rows={2}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Anything reception should know…"
          style={{ width: '100%' }}
        />
      </OperaSection>
    </OperaWindow>
  );
}
